/**
 * HR Org Chart — /hr/org-chart.
 *
 * The company's structural hierarchy: company → division → department →
 * office → team → people, arbitrary depth, sourced from the `org_units`
 * table + `teams.parent_unit_id` on GET /api/v1/hr/org-chart. Structural
 * only — it does NOT drive approver routing (that stays on
 * `approval_assignments`). Clicking a person reveals their primary
 * approver in a popover, where HR can reassign it inline.
 *
 * Gated on `flags.hrOrgChart`. Returns `null` when the flag is OFF so a
 * direct URL hit falls through to the 404 surface; the sidebar nav item
 * is also flag-gated.
 */
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { OrgChartEmployee } from '@lms/shared-types'
import { PageHead } from '../components/primitives/PageHead'
import { Card } from '../components/primitives/Card'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { flags } from '../lib/feature-flags'
import { getOrgChart } from '../api/hr-org-chart'
import { setPrimaryApprover } from '../api/hr-approval-assignments'
import { buildOrgChartIndex, buildOrgTree } from '../lib/org-chart'
import { OrgTreeView } from '../components/hr/org-chart/v2/OrgTreeView'
import { useOrgChartQuickActions } from '../components/hr/org-chart/v2/useOrgChartQuickActions'
import type {
  EmpLite,
  UnitLite,
} from '../components/hr/org-chart/v2/OrgStructureEditors'

const STALE_TIME_MS = 5 * 60_000

function HrOrgChartPageInner(): JSX.Element {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['hr', 'org-chart'],
    queryFn: ({ signal }) => getOrgChart(signal),
    staleTime: STALE_TIME_MS,
  })

  // Index powers approver resolution + the "change approver" candidate list.
  const index = useMemo(
    () => (query.data ? buildOrgChartIndex(query.data) : null),
    [query.data],
  )

  // Structural tree (company → division → dept → team → people).
  const orgTree = useMemo(
    () => (query.data ? buildOrgTree(query.data) : null),
    [query.data],
  )

  // Resolve a person → their primary approver, for the clickable member rows.
  const resolveApprover = useMemo(() => {
    return (employeeId: string): OrgChartEmployee | null => {
      if (!index) return null
      const approverId = index.primaryApproverByEmployee[employeeId] ?? null
      return approverId ? (index.employeesById.get(approverId) ?? null) : null
    }
  }, [index])

  // Candidates for the "Change approver" picker — every (non-system)
  // employee; the picker itself filters out the person being edited.
  const approverCandidates = useMemo(
    () =>
      index
        ? Array.from(index.employeesById.values())
            .map((e) => ({ id: e.id, name: e.name, jobTitle: e.jobTitle }))
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [index],
  )

  const setApproverMutation = useMutation({
    mutationFn: ({
      employeeId,
      approverId,
    }: {
      employeeId: string
      approverId: string
    }) => setPrimaryApprover(employeeId, approverId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
    },
  })

  const handleSetApprover = useMemo(
    () => async (employeeId: string, approverId: string) => {
      await setApproverMutation.mutateAsync({ employeeId, approverId })
    },
    [setApproverMutation],
  )

  // Structural CRUD inputs for the inline ⋮ quick-action menus.
  const unitLites = useMemo<UnitLite[]>(
    () =>
      (query.data?.orgUnits ?? [])
        .map((u) => ({ id: u.id, name: u.name, unitType: u.unitType }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [query.data],
  )
  const empLites = useMemo<EmpLite[]>(
    () =>
      (query.data?.employees ?? [])
        .map((e) => ({ id: e.id, name: e.name, jobTitle: e.jobTitle }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [query.data],
  )
  const teamLites = useMemo(
    () =>
      (query.data?.teams ?? []).map((t) => ({ id: t.id, name: t.name })),
    [query.data],
  )

  const quickActions = useOrgChartQuickActions({
    units: unitLites,
    employees: empLites,
    teams: teamLites,
  })

  return (
    <HrAccessGuard>
      <div className="page">
      <PageHead
        eyebrow="HR"
        title="Org chart"
        subtitle="The company structure by division, department, and team. Expand a unit to see its teams and people, or click a person to view and change their approver."
      />

      {query.isLoading ? (
        <Card padding={24}>
          <p style={{ fontSize: 14, color: 'var(--text-3)', margin: 0 }}>
            Loading org chart…
          </p>
        </Card>
      ) : query.isError ? (
        <Card padding={24} data-testid="org-chart-error">
          <p style={{ fontSize: 14, color: 'var(--danger-fg)', margin: 0 }}>
            Failed to load the org chart. Refresh the page or contact engineering
            if this persists.
          </p>
        </Card>
      ) : orgTree == null ? null : (
        <>
          <OrgTreeView
            tree={orgTree}
            resolveApprover={resolveApprover}
            candidates={approverCandidates}
            onSetApprover={handleSetApprover}
            unitActions={quickActions.unitActions}
            teamActions={quickActions.teamActions}
            teamCandidates={quickActions.teamCandidates}
            onSetTeam={quickActions.onSetTeam}
            onRemoveFromTeam={quickActions.onRemoveFromTeam}
            unassignedTeamId={quickActions.unassignedTeamId}
          />
          {quickActions.modals}
        </>
      )}
      </div>
    </HrAccessGuard>
  )
}

export function HrOrgChartPage(): JSX.Element | null {
  if (!flags.hrOrgChart) return null
  return <HrOrgChartPageInner />
}
